/**
 * Layout classes for the list layout. Structure only — no visual decisions
 * beyond tokens.
 *
 * `page` carries NO padding: the shell's `<main>` owns page gutters, so a list
 * page cannot double them. It uses `flex-1` rather than `min-h-full` because
 * the shell main is a flex column, which makes the page the thing that grows
 * and lets the table surface below it absorb the remaining height.
 *
 * `page` stretches to the full width the shell gives it at every breakpoint.
 * The table is the densest thing on the page, so it is also the thing that
 * benefits most from width — a capped measure would waste the screen it is
 * meant to use.
 */
export const styles = {
  /**
   * The page column.
   *
   * The gap between the header, the banner, the filter slot and the table is
   * `gap-3` on mobile and `gap-4` from `sm` up. On a phone the header, the
   * tabs strip and the filter bar already stack into a tall block of chrome,
   * and a 16px gap between each of them spends a fifth of a 700px viewport on
   * empty canvas before the first row is reached. 12px keeps the sections
   * distinct without the page reading as mostly gaps.
   */
  page: 'flex flex-1 flex-col gap-3 sm:gap-4',

  /**
   * The header: title, description and the module's toolbar actions.
   *
   * `gap-3` on mobile, `gap-4` from `sm` up, for the same reason as `page`:
   * the header is the first thing on the screen and its internal spacing is
   * spent before any content is.
   *
   * THE TITLE AND THE ACTIONS SHARE ONE ROW AT EVERY WIDTH.
   * The description is hidden below `sm` (see `description`), so on a phone the
   * text block holds only the title — one short line that has no reason to
   * claim a row of its own. The header is `flex-nowrap` so the two can never
   * split onto separate lines, the text block is `flex-1` so it absorbs
   * whatever width the toolbar does not need, and the toolbar is `shrink-0` so
   * the buttons keep their intrinsic width. `items-center` aligns the title's
   * baseline with the middle of the 32px buttons.
   *
   * The title is `truncate` as a last resort: the toolbar is `shrink-0`, so if
   * a module ever puts enough actions in the header to fill the row, the title
   * gives way rather than pushing the buttons off the right edge. The buttons
   * are the interactive controls and must stay whole and reachable; the title
   * is repeated in the document title and the breadcrumb.
   *
   * From `sm` up the description returns and the text block reverts to its
   * content width (`sm:w-auto sm:flex-none`) so the description can wrap
   * naturally beside the actions rather than being stretched to fill.
   *
   * `mb-3` below `sm` gives the header its own breathing room from the tabs
   * strip. The parent `page` already spaces its children with `gap-3`, but on a
   * phone the header is a single 32px row of title and buttons and the tabs
   * strip below it is a tinted band — with only the flex gap between them the
   * two read as one block. The margin separates the header from the chrome
   * beneath it. It is dropped from `sm` up, where the description makes the
   * header tall enough to stand apart on its own.
   */
  header: 'mb-3 flex flex-nowrap items-center justify-between gap-3 sm:mb-0 sm:gap-4',
  headerText: 'min-w-0 flex-1 sm:w-auto sm:flex-none',
  title: 'truncate text-xl font-semibold text-content sm:text-2xl',

  /**
   * The page description.
   *
   * Hidden below `sm`. On a phone the header is the first thing on the screen
   * and the description is the longest thing in it — two or three lines of
   * muted prose that push the first row of data further down the viewport than
   * the title and the actions do. The title already names the page and the
   * actions already say what can be done with it, so the sentence is the part
   * that can wait until there is room for it. It stays in the DOM for a screen
   * reader at every width.
   */
  description: 'hidden text-sm text-content-muted sm:block',
  toolbar: 'flex shrink-0 flex-wrap items-center justify-end gap-2',
  banner: 'flex flex-col',
  filters: 'rounded-surface border border-border bg-surface-raised p-4',

  /**
   * The non-ready holder (error, empty, denied, loading).
   *
   * It carries NO card chrome of its own. Every one of those states already
   * renders its own bordered card, so a card here produced a box inside a box
   * with two borders and two near-identical fills. This is a bare column that
   * lets the state BE the card.
   *
   * `[&>*]:flex-1` makes that single card fill the leftover height, so a short
   * message centres in the page instead of stranding a large void beneath it.
   */
  surface: 'flex flex-1 flex-col [&>*]:flex-1',

  bulkBar:
    'flex items-center gap-3 rounded-control border border-border bg-surface-raised px-4 py-2 text-sm',

  tableWrap: 'overflow-x-auto rounded-surface border border-border bg-surface-raised',
  table: 'w-full border-collapse text-left text-sm',
  caption: 'sr-only',
  th: 'px-4 py-3 text-xs font-medium tracking-wide text-content-muted uppercase',
  td: 'px-4 py-3 text-content',
  rowSelected: 'bg-surface-sunken',
  sortButton: 'inline-flex items-center gap-1 hover:text-content',
  sortIndicator: 'text-content-subtle',
  rowActions: 'flex items-center gap-2',

  pagination: 'flex items-center justify-end gap-3',
  pageStatus: 'text-sm text-content-muted',
} as const;